import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo, Fragment, JSX } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import SubAccountSearchForm, { SubAccountStatus } from './SubAccountSearchForm';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useAccountInfo } from '@/providers/AccountInfo.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { useLockedFeatureDialogue } from '@/providers/LockedFeatureDialogue.provider';
import { useClientLogin } from '@/providers/ClientLogin.provider';
import Table, { createTableProps, Row } from './Table/Table';
import TransactionStatus from '@/components/UI/TransactionStatus/TransactionStatus';
import { Title, TableFiltersContainer } from '@/components/UI';
import useFetchSubAccounts from '@/hooks/useApi/useFetchSubAccounts';
import useBlockAccounts from '@/hooks/useApi/useBlockAccounts';
import useDeactivateSubAccount from '@/hooks/useApi/useDeactivateSubAccount';
import styles from './SubAccount.module.scss';
import OrgChartContainer from './OrgChartContainer/OrgChartContainer';
import { Button, UtilButton, Table as TableUI } from '@/components/UI';
import { useTranslation } from 'react-i18next';
import AccountPermissionsDrawer from './Permissions/AccountPermissionsDrawer';
import UserManagementDrawer from './UserManagement/UserManagementDrawer';
import { useDialog } from '@/providers/Dialog.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { BlockDialog, UnBlockDialog, BlockAccountDialog, UnBlockAccountDialog } from './BlockingDialog';
import { BodyText, MaterialSymbol, DropdownMenu } from 'vopay';
import { useDataFetchIndicator } from '@/hooks';
import { useToast } from '@/providers/Toast.provider';

const { Box, formattedCurrency, useUpdateQueryParams, withStyles, Card, usePagination, Pagination } = vp;

const classes = withStyles(styles);

type QueryParams = {
  status: string;
  currency: string;
  country: string;
  searchTerm: string;
};

type FC = FunctionComponent;
export type NestedSubAccount = vp.SubAccountsListsShow & {
  SubAccounts?: NestedSubAccount[];
  Key: string;
};
export type FlatSubAccountWithTree = vp.SubAccountsListsShow & {
  tree: string[]; // Array of parent IDs
};
const SubAccountsListContainer: FC = () => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const status: SubAccountStatus = useMemo(() => {
    const param = queryParams.get('status');
    if (param === null) return 'Active'; // no param = default to Active on initial load
    if (param === 'all') return ''; // explicit "show all" sentinel set by search clear
    return param as SubAccountStatus;
  }, [queryParams]);
  const currency = useMemo(() => queryParams.get('currency') || '', [queryParams]);
  const country = useMemo(() => queryParams.get('country') || '', [queryParams]);
  const searchTerm = useMemo(() => queryParams.get('searchTerm') || '', [queryParams]);
  const toast = useToast();
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.List' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const { permissions } = usePermissions();
  const [subAccountsResponse, , , fetchSubAccounts] = useFetchSubAccounts({});
  const [orgChartResponse, , , fetchOrgChartSubAccounts] = useFetchSubAccounts({});
  const { pageCount, pageIndex, setPageIndex, offset } = usePagination(
    subAccountsResponse.PaginationMetaData?.TotalRecords || 0,
    { pageSize: 20 },
  );
  const {
    pageIndex: orgChartPageIndex,
    setPageIndex: setOrgChartPageIndex,
    offset: orgChartOffset,
  } = usePagination(orgChartResponse.PaginationMetaData?.TotalRecords || 0, { pageSize: 10 });
  const { blockAccounts, unblockAccounts, blockAccount, unblockAccount } = useBlockAccounts();
  const [, setLoading] = useDataFetchIndicator();
  const { onSubNavClick } = useNavMenu();
  const { setNewCurrencyInfo } = useAccountInfo();
  const [nestedSubAccounts, setNestedSubAccounts] = useState<NestedSubAccount[]>([]);
  const [orgChartNestedSubAccounts, setOrgChartNestedSubAccounts] = useState<NestedSubAccount[]>([]);
  const [flatSubAccountWithTree, setFlatSubAccountWithTree] = useState<FlatSubAccountWithTree[]>([]);
  const [flatSubAccountFiltered, setFlatSubAccountFiltered] = useState<FlatSubAccountWithTree[]>([]);
  const hasSearch = useMemo(
    () => searchTerm !== '' || status === 'NotActive' || currency !== '' || country !== '',
    [searchTerm, status, currency, country],
  );
  const showNestedView = useMemo(
    () => !hasSearch || (searchTerm === '' && currency === '' && country === '' && status === 'Active'),
    [hasSearch, searchTerm, currency, country, status],
  );
  const [showOrgView, setShowOrgView] = useState(false);
  const countries = ['Canada', 'United States'];
  const [isPermissionsDrawerOpen, setIsPermissionsDrawerOpen] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string>();
  const [selectedAccountName, setSelectedAccountName] = useState<string>();
  const [selectedAccountCurrency, setSelectedAccountCurrency] = useState<string>();
  const [selectedAccountToken, setSelectedAccountToken] = useState<string>();
  const dialogue = useDialog();
  const alertDialog = useAlertDialog();
  const [showTransactionFilter, setShowTransactionFilter] = useState(true);
  const lockedFeatures = useLockedFeatures();
  const { updateIsLocked } = useLockedFeatureDialogue();
  const [isUserManagementDrawerOpen, setIsUserManagementDrawerOpen] = useState(false);
  const { deactivateSubaccount } = useDeactivateSubAccount();

  const {
    response: { clientAccountsEnabled, AccountID, AccountName, ShowSubAccountOrgChart, AccountType },
  } = useClientLogin();

  async function handleDeactivationDialogue(accountId: string) {
    const result = await alertDialog.caution(
      t('DeactivateDialog.title', {
        defaultValue: 'Deactivate Sub-Account?',
      }),
      <div className={styles.dialogContent}>
        {t('DeactivateDialog.content', {
          defaultValue: 'Are you sure you want to deactivate this sub-account?',
        })}
      </div>,
      {
        abortLabel: tGlobal('Buttons.cancel', { defaultValue: 'Cancel' }),
        confirmLabel: t('Buttons.deactivate', { defaultValue: 'Deactivate' }),
      },
    );

    if (result === 'confirm') {
      try {
        setLoading(true);
        await deactivateSubaccount({
          SubaccountID: accountId,
        });
        await fetchSubAccounts({
          PortalUser: AccountID,
          ShowToken: true,
          SearchText: searchTerm || undefined,
          ActiveOnly: status === 'Active' ? true : status === 'NotActive' ? false : undefined,
          Currency: currency || undefined,
          Country: country || undefined,
          Limit: 20,
          Offset: offset,
        });
      } catch (err) {
        if (err instanceof Error) toast.neutral(err.message);
      } finally {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      const resp = await fetchSubAccounts({
        PortalUser: AccountID,
        ShowToken: true,
        SearchText: searchTerm || undefined,
        ActiveOnly: status === 'Active' ? true : status === 'NotActive' ? false : undefined,
        Currency: currency || undefined,
        Country: country || undefined,
        Limit: 20,
        Offset: offset,
      });
      if (!resp.Success) {
        toast.neutral(resp.ErrorMessage);
      }
    };
    fetchData();
  }, [status, currency, country, searchTerm, offset]);

  const search = ({
    searchTerm: newSearchTerm,
    status: newStatus,
    currency: newCurrency,
    country: newCountry,
  }: {
    searchTerm: string;
    status: SubAccountStatus;
    currency: string;
    country: string;
  }) => {
    setPageIndex(0);
    updateQueryParams({
      status: newStatus || 'all',
      currency: newCurrency,
      country: newCountry,
      searchTerm: newSearchTerm,
    });
  };

  useEffect(() => {
    const rawSubAccounts = subAccountsResponse.Subaccounts || [];
    const mappedSubAccounts = mapBalances(rawSubAccounts);

    const { nested } = buildNested(AccountID, AccountName, rawSubAccounts);
    setNestedSubAccounts(nested);

    setFlatSubAccountWithTree(buildFlatWithTree(mappedSubAccounts));
  }, [subAccountsResponse]);

  useEffect(() => {
    setFlatSubAccountFiltered(
      country ? flatSubAccountWithTree.filter((data) => data.Country === country) : flatSubAccountWithTree,
    );
  }, [flatSubAccountWithTree, country]);

  useEffect(() => {
    if (!showOrgView) return;
    const fetchData = async () => {
      const resp = await fetchOrgChartSubAccounts({
        PortalUser: AccountID,
        ShowToken: true,
        Limit: 10,
        Offset: orgChartOffset,
      });
      if (!resp.Success) toast.neutral(resp.ErrorMessage);
    };
    fetchData();
  }, [showOrgView, orgChartOffset]);

  useEffect(() => {
    const rawSubAccounts = orgChartResponse.Subaccounts || [];
    const { nested } = buildNested(AccountID, AccountName, rawSubAccounts);
    setOrgChartNestedSubAccounts(nested);
  }, [orgChartResponse]);

  // Determine if all accounts are blocked/locked
  const allAccountsBlocked = useMemo(() => {
    const subAccounts = subAccountsResponse.Subaccounts || [];
    if (subAccounts.length === 0) return false;

    const allAccountBalances = mapBalances(subAccounts);
    return allAccountBalances.every((account) => account.AccountStatus === 'Blocked');
  }, [subAccountsResponse]);

  // Filter nested accounts for table view only (when status is Active)
  const filteredNestedForTable = useMemo(() => {
    return status === 'Active' ? filterActiveAccounts(nestedSubAccounts) : nestedSubAccounts;
  }, [nestedSubAccounts, status]);

  const tableProps = createTableProps({
    id: 'subAccountsList',

    hasPlaceholderArrows: true,
    defaultExpanded: true,
    rows: mapNestedSubAccounts(filteredNestedForTable),
    columns: [
      {
        id: 'accountName',
        header: t('Attributes.accountName', { defaultValue: 'Account Name' }),
        align: 'left',

        template: ({ data }) => {
          return (
            <TableUI.Link
              to={{
                pathname: `/sub-accounts-list/details/${data.AccountID.replace(/\s+/g, '')}/${data.Currency}`,
                state: { data },
              }}
            >
              {data.AccountName}
            </TableUI.Link>
          );
        },
      },
      {
        id: 'status',
        header: t('Attributes.status', { defaultValue: 'Status' }),
        align: 'left',
        template: ({ data }) => (
          <TransactionStatus
            status={data.AccountStatus === 'Blocked' ? 'blocked' : data.IsActive ? 'active' : 'not active'}
            label={
              data.AccountStatus === 'Blocked'
                ? t('Attributes.blockedBadge', { defaultValue: 'Blocked' })
                : data.IsActive
                  ? t('Attributes.activeBadge', { defaultValue: 'Active' })
                  : t('Attributes.notActiveBadge', { defaultValue: 'Not Active' })
            }
          />
        ),
        style: {
          whiteSpace: 'nowrap',
        },
      },
      {
        id: 'country',
        header: t('Attributes.country', { defaultValue: 'Country' }) as string,
        field: 'Country',
        align: 'left',
      },
      {
        id: 'pendingBalance',
        header: t('Attributes.pendingBalance', { defaultValue: 'Pending Balance' }),
        align: 'left',
        template: ({ data }) => (
          <span style={{ color: allAccountsBlocked ? '#2D2D2D0D' : 'inherit' }}>
            {`${data.Currency} $${formattedCurrency(data.PendingBalance)}`}
          </span>
        ),
      },
      {
        id: 'availableBalance',
        header: t('Attributes.availableBalance', { defaultValue: 'Available Balance' }),
        align: 'left',
        template: ({ data }) => (
          <span style={{ color: allAccountsBlocked ? '#2D2D2D0D' : 'inherit' }}>
            {`${data.Currency} $${formattedCurrency(data.AvailableBalance)}`}
          </span>
        ),
        hasDivider: false,
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box gap="sm" jc="fe" ai="center">
            {!clientAccountsEnabled && data.IsActive && permissions.CreateAccountTransfer && (
              <Button
                id={`view-sub-account-${data.AccountID.replace(/\s+/g, '')}`}
                size="smallest"
                colorScheme="secondary"
                onClick={() => {
                  onSubNavClick('accountTransfer', '/sub-account-transfer', 'Account Transfer');
                  setNewCurrencyInfo(data.Currency);
                  history.push({
                    pathname: '/sub-account-transfer',
                    state: { token: data.Token, currency: data.Currency },
                  });
                }}
              >
                {t('Table.Row.transferButton', { defaultValue: 'Transfer' })}
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button size="smallest" colorScheme="secondary" iconRight="keyboard_arrow_down">
                  {t('Table.Row.actionsButton', { defaultValue: 'Actions' })}
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                <DropdownMenu.Item
                  onSelect={() => {
                    setSelectedAccountId(data.AccountID);
                    setSelectedAccountName(data.AccountName);
                    setSelectedAccountCurrency(data.Currency);
                    setIsPermissionsDrawerOpen(true);
                  }}
                  left={<MaterialSymbol icon="settings" size={18} />}
                >
                  {t('Table.Row.permissionsButton', { defaultValue: 'Permissions' })}
                </DropdownMenu.Item>

                <DropdownMenu.Item
                  onSelect={() => {
                    setSelectedAccountName(data.AccountName);
                    setSelectedAccountToken(data.Token);
                    setIsUserManagementDrawerOpen(true);
                  }}
                  left={<MaterialSymbol icon="manage_accounts" size={18} />}
                >
                  {t('Table.Row.userManagementButton', { defaultValue: 'User Management' })}
                </DropdownMenu.Item>

                {/* <DropdownMenu.Item onSelect={() => {}} left={<MaterialSymbol icon="task_alt" size={18} />}>
                  {t('Table.Row.approvalWorkflowButton', { defaultValue: 'Approval Workflow' })}
                </DropdownMenu.Item> */}

                {(data.SubAccountType === 'division' || data.SubAccountType === 'ledger') && data.IsActive && (
                  <DropdownMenu.Item
                    onSelect={() => {
                      handleDeactivationDialogue(data.AccountID);
                    }}
                    left={<MaterialSymbol icon="remove_circle_outline" color="#e8160b" size={18} />}
                    className={classes('deactivate')}
                  >
                    {t('Table.Row.deactivateButton', { defaultValue: 'Deactivate' })}
                  </DropdownMenu.Item>
                )}

                {AccountType === 'Secured Account' &&
                  !!permissions.ManageBlockedAccounts &&
                  (data.AccountStatus === 'Blocked' ? (
                    <DropdownMenu.Item
                      onSelect={() => handleUnblockAccount(data.AccountID, data.AccountName)}
                      left={<MaterialSymbol icon="lock_open" size={18} />}
                    >
                      {t('Table.Row.unblockButton', { defaultValue: 'Unblock Sub-Account' })}
                    </DropdownMenu.Item>
                  ) : (
                    <DropdownMenu.Item
                      onSelect={() => handleBlockAccount(data.AccountID, data.AccountName)}
                      colorScheme="destructive"
                      left={<MaterialSymbol icon="block" size={18} />}
                    >
                      {t('Table.Row.blockButton', { defaultValue: 'Block Sub-Account' })}
                    </DropdownMenu.Item>
                  ))}
              </DropdownMenu.Content>
            </DropdownMenu>
          </Box>
        ),
      },
    ],
  });
  const searchTableProps = createTableProps({
    id: 'subAccountsList',
    hasPlaceholderArrows: false,

    rows: mapFlatSubAccountWithTree(flatSubAccountFiltered),
    columns: [
      {
        id: 'accountName',
        header: t('Attributes.accountName', { defaultValue: 'Account Name' }),
        align: 'left',
        template: ({ data }) => {
          const onClick = (value: string) => {
            search({ status: '', currency, country: '', searchTerm: value });
          };
          return (
            <Box flx="col" ai="fs">
              {getBreadcrumbs(data.tree, onClick)}
              <TableUI.Link
                to={{
                  pathname: `/sub-accounts-list/details/${data.AccountID.replace(/\s+/g, '')}/${data.Currency}`,
                  state: { data },
                }}
              >
                {data.AccountName}
              </TableUI.Link>
            </Box>
          );
        },
      },
      {
        id: 'status',
        header: t('Attributes.status', { defaultValue: 'Status' }),
        align: 'left',
        template: ({ data }) => (
          <TransactionStatus
            status={data.AccountStatus === 'Blocked' ? 'blocked' : data.IsActive ? 'active' : 'not active'}
            label={
              data.AccountStatus === 'Blocked'
                ? t('Attributes.blockedBadge', { defaultValue: 'Blocked' })
                : data.IsActive
                  ? t('Attributes.activeBadge', { defaultValue: 'Active' })
                  : t('Attributes.notActiveBadge', { defaultValue: 'Not Active' })
            }
          />
        ),
        style: {
          whiteSpace: 'nowrap',
        },
      },
      {
        id: 'country',
        header: t('Attributes.country', { defaultValue: 'Country' }) as string,
        field: 'Country',
        align: 'left',
      },
      {
        id: 'pendingBalance',
        header: t('Attributes.pendingBalance', { defaultValue: 'Pending Balance' }),
        align: 'left',
        template: ({ data }) => (
          <span style={{ color: allAccountsBlocked ? '#2D2D2DB3' : 'inherit' }}>
            {`${data.Currency} $${formattedCurrency(data.PendingBalance)}`}
          </span>
        ),
      },
      {
        id: 'availableBalance',
        header: t('Attributes.availableBalance', { defaultValue: 'Available Balance' }),
        align: 'left',
        template: ({ data }) => (
          <span style={{ color: allAccountsBlocked ? '#2D2D2DB3' : 'inherit' }}>
            {`${data.Currency} $${formattedCurrency(data.AvailableBalance)}`}
          </span>
        ),
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box gap="sm" jc="fe" ai="center">
            {!clientAccountsEnabled && data.IsActive && permissions.CreateAccountTransfer && (
              <Button
                id={`view-sub-account-${data.AccountID.replace(/\s+/g, '')}`}
                colorScheme="secondary"
                size="smallest"
                onClick={() => {
                  onSubNavClick('accountTransfer', '/sub-account-transfer', 'Account Transfer');
                  setNewCurrencyInfo(data.Currency);
                  history.push({
                    pathname: '/sub-account-transfer',
                    state: { token: data.Token, currency: data.Currency },
                  });
                }}
              >
                {t('Table.Row.transferButton', { defaultValue: 'Transfer' })}
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button size="smallest" colorScheme="secondary" iconRight="keyboard_arrow_down">
                  {t('Table.Row.actionsButton', { defaultValue: 'Actions' })}
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                <DropdownMenu.Item
                  onSelect={() => {
                    setSelectedAccountId(data.AccountID);
                    setSelectedAccountName(data.AccountName);
                    setSelectedAccountCurrency(data.Currency);
                    setIsPermissionsDrawerOpen(true);
                  }}
                  left={<MaterialSymbol icon="settings" fillAmount={1} size={18} />}
                  style={{ gap: '21px' }}
                >
                  {t('Table.Row.permissionsButton', { defaultValue: 'Permissions' })}
                </DropdownMenu.Item>

                <DropdownMenu.Item
                  onSelect={() => {
                    setSelectedAccountName(data.AccountName);
                    setSelectedAccountToken(data.Token);
                    setIsUserManagementDrawerOpen(true);
                  }}
                  left={<MaterialSymbol icon="manage_accounts" size={18} />}
                >
                  {t('Table.Row.userManagementButton', { defaultValue: 'User Management' })}
                </DropdownMenu.Item>

                {(data.SubAccountType === 'division' || data.SubAccountType === 'ledger') && data.IsActive && (
                  <DropdownMenu.Item
                    onSelect={() => {
                      handleDeactivationDialogue(data.AccountID);
                    }}
                    left={<MaterialSymbol icon="remove_circle_outline" color="#e8160b" size={18} />}
                    className={classes('deactivate')}
                  >
                    {t('Table.Row.deactivateButton', { defaultValue: 'Deactivate' })}
                  </DropdownMenu.Item>
                )}

                {/* <DropdownMenu.Item onSelect={() => {}} left={<MaterialSymbol icon="task_alt" size={18} />}>
                  {t('Table.Row.approvalWorkflowButton', { defaultValue: 'Approval Workflow' })}
                </DropdownMenu.Item> */}

                {AccountType === 'Secured Account' &&
                  !!permissions.ManageBlockedAccounts &&
                  (data.AccountStatus === 'Blocked' ? (
                    <DropdownMenu.Item
                      onSelect={() => handleUnblockAccount(data.AccountID, data.AccountName)}
                      left={<MaterialSymbol icon="lock_open" size={18} />}
                      style={{ gap: '21px' }}
                    >
                      {t('Table.Row.unblockButton', { defaultValue: 'Unblock Sub-Account' })}
                    </DropdownMenu.Item>
                  ) : (
                    <DropdownMenu.Item
                      onSelect={() => handleBlockAccount(data.AccountID, data.AccountName)}
                      colorScheme="destructive"
                      left={<MaterialSymbol icon="block" size={18} />}
                      style={{ gap: '21px' }}
                    >
                      {t('Table.Row.blockButton', { defaultValue: 'Block Sub-Account' })}
                    </DropdownMenu.Item>
                  ))}
              </DropdownMenu.Content>
            </DropdownMenu>
          </Box>
        ),
      },
    ],
  });
  const handleBlockAccounts = async () => {
    try {
      setLoading(true);
      await blockAccounts();
      const resp = await fetchSubAccounts({
        PortalUser: AccountID,
        ShowToken: true,
        SearchText: searchTerm || undefined,
        ActiveOnly: status === 'Active' ? true : status === 'NotActive' ? false : undefined,
        Currency: currency || undefined,
        Limit: 20,
        Offset: offset,
      });
      if (!resp.Success) {
        throw new Error(resp.ErrorMessage);
      }
      toast.neutral(t('blockSuccessToast', { defaultValue: 'All Sub-Accounts have been blocked' }));
    } catch (error) {
      if (error instanceof Error) {
        toast.neutral(error.message);
      }
      console.error('Failed to block accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockAccounts = async () => {
    try {
      setLoading(true);
      await unblockAccounts();
      const resp = await fetchSubAccounts({
        PortalUser: AccountID,
        ShowToken: true,
        SearchText: searchTerm || undefined,
        ActiveOnly: status === 'Active' ? true : status === 'NotActive' ? false : undefined,
        Currency: currency || undefined,
        Limit: 20,
        Offset: offset,
      });
      if (!resp.Success) {
        throw new Error(resp.ErrorMessage);
      }
      toast.neutral(t('unblockSuccessToast', { defaultValue: 'All Sub-Accounts have been unblocked' }));
    } catch (error) {
      if (error instanceof Error) {
        toast.neutral(error.message);
      }
      console.error('Failed to unblock accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBlockAccount = async (accountID: string, accountName: string) => {
    const confirmed = await dialogue.open<true>((props) => <BlockAccountDialog {...props} accountName={accountName} />);
    if (!confirmed) return;
    try {
      setLoading(true);
      await blockAccount(accountID);
      const resp = await fetchSubAccounts({
        PortalUser: AccountID,
        ShowToken: true,
        SearchText: searchTerm || undefined,
        ActiveOnly: status === 'Active' ? true : status === 'NotActive' ? false : undefined,
        Currency: currency || undefined,
        Limit: 20,
        Offset: offset,
      });
      if (!resp.Success) throw new Error(resp.ErrorMessage);
      toast.neutral(t('blockAccountSuccessToast', { accountName, defaultValue: '{{accountName}} has been blocked' }));
    } catch (error) {
      if (error instanceof Error) alertDialog.error(t('errorTitle', { defaultValue: 'Error' }), error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockAccount = async (accountID: string, accountName: string) => {
    const confirmed = await dialogue.open<true>((props) => (
      <UnBlockAccountDialog {...props} accountName={accountName} />
    ));
    if (!confirmed) return;
    try {
      setLoading(true);
      await unblockAccount(accountID);
      const resp = await fetchSubAccounts({
        PortalUser: AccountID,
        ShowToken: true,
        SearchText: searchTerm || undefined,
        ActiveOnly: status === 'Active' ? true : status === 'NotActive' ? false : undefined,
        Currency: currency || undefined,
        Limit: 20,
        Offset: offset,
      });
      if (!resp.Success) throw new Error(resp.ErrorMessage);
      toast.neutral(
        t('unblockAccountSuccessToast', { accountName, defaultValue: '{{accountName}} has been unblocked' }),
      );
    } catch (error) {
      if (error instanceof Error) alertDialog.error(t('errorTitle', { defaultValue: 'Error' }), error.message);
    } finally {
      setLoading(false);
    }
  };

  const blockSubAccounts = async () => {
    const confirmed = await dialogue.open(BlockDialog);
    if (confirmed) {
      await handleBlockAccounts();
    }
  };

  const unblockSubAccounts = async () => {
    const confirmed = await dialogue.open(UnBlockDialog);
    if (confirmed) {
      await handleUnblockAccounts();
    }
  };

  return (
    <Box flx="row" jc="center" className={classes('subAccountsListContainer')}>
      <Box flx="col" gapSize="33px" boxWidth="100%">
        <Box jc="sb" width="100%">
          <Title>{t('title', { defaultValue: 'Sub-Account List' })}</Title>
          <Box gapSize="16px" ai="center">
            {showOrgView && ShowSubAccountOrgChart && (
              <UtilButton
                onClick={() => {
                  setShowOrgView(false);
                }}
                iconLeftProps={{ icon: 'table_rows', fillAmount: 1 }}
              >
                {t('switchViewButton', { defaultValue: 'Switch View' })}
              </UtilButton>
            )}
            {!showOrgView && ShowSubAccountOrgChart && (
              <UtilButton
                iconLeft="lan"
                onClick={() => {
                  setShowOrgView(true);
                }}
              >
                {t('switchViewButton', { defaultValue: 'Switch View' })}
              </UtilButton>
            )}
            {AccountType === 'Secured Account' && permissions.ManageBlockedAccounts && (
              <>
                {!allAccountsBlocked && (
                  <Button colorScheme="secondary" onClick={blockSubAccounts}>
                    {t('blockSubAccountsButton', { defaultValue: 'Block Sub-Accounts' })}
                  </Button>
                )}
                {allAccountsBlocked && (
                  <Button colorScheme="secondary" onClick={unblockSubAccounts}>
                    {t('unblockSubAccountsButton', { defaultValue: 'Unblock Sub-Accounts' })}
                  </Button>
                )}
              </>
            )}
            {permissions.AddSubaccounts &&
              (lockedFeatures.SubAccountPermissions ? (
                <Button
                  id="add-sub-account"
                  colorScheme="primary"
                  onClick={() => {
                    onSubNavClick(
                      'createSubAccount',
                      '/create-sub-account',
                      t('createModuleName', { defaultValue: 'Create Sub-Account' }),
                    );
                    history.push('/create-sub-account');
                  }}
                  iconLeft="add"
                >
                  {t('createSubAccountButton', { defaultValue: 'Sub-Account' })}
                </Button>
              ) : (
                <Button
                  id="add-sub-account"
                  onClick={() => updateIsLocked(true, t('createModuleName', { defaultValue: 'Create Sub-Account' }))}
                  iconLeftProps={{ icon: 'lock' }}
                  colorScheme="primary"
                  className={styles.lockedButton}
                >
                  {t('addSubAccountButton', { defaultValue: 'Add Account' })}
                </Button>
              ))}
          </Box>
        </Box>
        {AccountType === 'Secured Account' && allAccountsBlocked && (
          <div className={classes('infoBox')}>
            <div className={classes('infoBoxHeader')}>
              <MaterialSymbol color="#2F82FF" size="20px" icon="block" />
              <BodyText weight="em">{t('blockedInfoTitle', { defaultValue: 'Sub-Accounts are Blocked' })}</BodyText>
            </div>
            <BodyText className={classes('infoBoxDescription')}>
              {t('blockedInfoDescription', {
                defaultValue:
                  'Outbound transactions are disabled and Inbound transactions will deposit to the parent account.',
              })}
            </BodyText>
          </div>
        )}
        {!showOrgView && (
          <Card
            style={{
              padding: '24px 16px',
            }}
          >
            <Box flx="col" pb="md" base="8">
              <TableFiltersContainer
                open={showTransactionFilter}
                onOpenChange={setShowTransactionFilter}
                filtersApplied={hasSearch}
              >
                <Box flx="col" pb="sm" base="8" ai="fe">
                  <TableFiltersContainer.Trigger />
                </Box>

                <TableFiltersContainer.Content>
                  <SubAccountSearchForm
                    status={status}
                    currency={currency}
                    country={country}
                    countries={countries}
                    searchTerm={searchTerm}
                    search={(values) => search(values)}
                  />
                </TableFiltersContainer.Content>
              </TableFiltersContainer>
            </Box>

            <Box className={classes('subAccountsTableContainer', 'scrollbar')}>
              {showNestedView && <Table {...tableProps} />}
              {!showNestedView && <Table {...searchTableProps} />}
            </Box>
            <Box flx="row" jc="center" mt="md" base="8">
              {pageCount > 1 && (
                <Pagination
                  pageCount={pageCount}
                  forcePageNumber={pageIndex}
                  handlePageClick={(e) => setPageIndex(e.selected)}
                />
              )}
            </Box>
          </Card>
        )}
        {showOrgView && (
          <OrgChartContainer
            subAccounts={orgChartNestedSubAccounts}
            accountBlocked={allAccountsBlocked}
            canManageBlocking={AccountType === 'Secured Account' && !!permissions.ManageBlockedAccounts}
            onBlockAccount={handleBlockAccount}
            onUnblockAccount={handleUnblockAccount}
            refresh={async () => {
              const resp = await fetchOrgChartSubAccounts({
                PortalUser: AccountID,
                ShowToken: true,
                Limit: 10,
                Offset: orgChartOffset,
              });
              if (!resp.Success) toast.neutral(resp.ErrorMessage);
            }}
            hasRootNextServerPage={(orgChartResponse.PaginationMetaData?.TotalRecords || 0) > orgChartOffset + 10}
            hasRootPrevServerPage={orgChartOffset > 0}
            rootNextPage={() => setOrgChartPageIndex(orgChartPageIndex + 1)}
            rootPrevPage={() => setOrgChartPageIndex(Math.max(0, orgChartPageIndex - 1))}
            rootPageSize={10}
          />
        )}
      </Box>
      <AccountPermissionsDrawer
        isOpen={isPermissionsDrawerOpen}
        onClose={() => setIsPermissionsDrawerOpen(false)}
        accountId={selectedAccountId}
        accountName={selectedAccountName}
        currency={selectedAccountCurrency}
      />
      {selectedAccountToken && selectedAccountName && (
        <UserManagementDrawer
          accountToken={selectedAccountToken}
          accountName={selectedAccountName}
          open={isUserManagementDrawerOpen}
          onClose={() => setIsUserManagementDrawerOpen(false)}
        />
      )}
    </Box>
  );

  function mapBalances(subAccounts: vp.SubAccountsLists[]): vp.SubAccountsListsShow[] {
    const subAccountsShow: vp.SubAccountsListsShow[] = [];
    subAccounts.forEach((subAccount) => {
      const balances = Object.values(subAccount.Balances || {});
      if (balances.length === 0) {
      } else {
        balances.forEach((balance) => {
          subAccountsShow.push({ ...subAccount, ...balance, ParentAccounts: subAccount.ParentAccounts });
        });
      }
    });

    return subAccountsShow;
  }

  function filterActiveAccounts(accounts: NestedSubAccount[]): NestedSubAccount[] {
    return accounts
      .filter((account) => account.IsActive)
      .map((account) => ({
        ...account,
        SubAccounts: account.SubAccounts ? filterActiveAccounts(account.SubAccounts) : undefined,
      }));
  }

  function buildFlatWithTree(subAccounts: vp.SubAccountsListsShow[]): FlatSubAccountWithTree[] {
    return subAccounts.map((account) => {
      const parents = account.ParentAccounts;
      if (parents.length === 0) return { ...account, tree: [] };

      const root = parents.find((p) => p.ParentAccountID === null);
      if (!root) return { ...account, tree: parents.map((p) => p.AccountName) };

      const chain: string[] = [root.AccountName];
      let current = parents.find((p) => p.ParentAccountID === root.AccountID);
      while (current) {
        chain.push(current.AccountName);
        const next = parents.find((p) => p.ParentAccountID === current!.AccountID);
        current = next;
      }

      // slice(1) excludes the root (the logged-in account) to match the nested view tree convention
      return { ...account, tree: chain.slice(1) };
    });
  }

  function buildNested(
    _parentId: string,
    _parentName: string,
    subAccounts: vp.SubAccountsLists[],
  ): { nested: NestedSubAccount[] } {
    function toNestedRows(account: vp.SubAccountsLists): NestedSubAccount[] {
      const childRows = (account.SubAccounts || []).flatMap(toNestedRows);
      return mapBalances([account]).map((balanceRow) => ({
        ...balanceRow,
        SubAccounts: childRows,
        Key: `${balanceRow.AccountID}-${balanceRow.Currency}`,
      }));
    }

    return { nested: subAccounts.flatMap(toNestedRows) };
  }
  function mapNestedSubAccounts(nestedSubAccounts: NestedSubAccount[]): Row<NestedSubAccount>[] {
    return (
      nestedSubAccounts.map((account) => ({
        id: `${account.AccountID}-${account.Currency}`,
        data: account,
        subRows: (account.SubAccounts || []).length > 0 ? mapNestedSubAccounts(account.SubAccounts || []) : undefined,
      })) || []
    );
  }
  function mapFlatSubAccountWithTree(flatSubAccountWithTree: FlatSubAccountWithTree[]): Row<FlatSubAccountWithTree>[] {
    return (
      flatSubAccountWithTree.map((account) => ({
        id: `${account.AccountID}-${account.Currency}`,
        data: account,
      })) || []
    );
  }
  function getBreadcrumbs(tree: string[], onClick: (value: string) => void): JSX.Element {
    if (tree.length === 0) return <></>;

    const [expanded, setExpanded] = useState(false);
    const toggleExpanded = () => setExpanded(!expanded);

    const renderBreadcrumbItem = (item: string, index: number) => (
      <Fragment key={item}>
        <button type="button" onClick={() => onClick(item)}>
          {item}
        </button>
        {index < tree.length - 1 && ' > '}
      </Fragment>
    );

    const renderCollapsedBreadcrumbs = () => (
      <>
        {renderBreadcrumbItem(tree[0], 0)}
        {'>'}
        <button type="button" onClick={toggleExpanded}>
          ...
        </button>
        {'>'}
        {renderBreadcrumbItem(tree[tree.length - 1], tree.length - 1)}
      </>
    );

    const breadcrumbs = (
      <span className={styles.breadcrumbs}>
        {tree.length <= 2 || expanded ? tree.map(renderBreadcrumbItem) : renderCollapsedBreadcrumbs()}
      </span>
    );

    return <nav aria-label={t('breadcrumbsLabel', { defaultValue: 'Breadcrumbs' })}>{breadcrumbs}</nav>;
  }
};

export default SubAccountsListContainer;
